import { useState, useEffect, CSSProperties, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ToggleStatCard } from '@/components/ui/ToggleStatCard';
import { Button } from '@/components/ui/Button';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserIcon,
  DocumentTextIcon,
  LinkIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import {
  MagnifyingGlassIcon as MagnifyingGlassIconSolid,
  ExclamationTriangleIcon as ExclamationTriangleIconSolid,
} from '@heroicons/react/24/solid';
import { investigationsService, Investigation, InvestigationStats } from '@/services/investigations.service';
import { VirtualList } from '@/components/common/VirtualList';
import { sanitizeHtml } from '@/utils/sanitize';
import { SECURITY_CATEGORIES } from '@corporate-security/types';

// Animation variants - defined outside component to avoid recreation
const LIST_CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03, // Fast stagger for list items
    },
  },
};

const STAT_CARD_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};

// Item height for virtualization (includes padding)
const INVESTIGATION_ITEM_HEIGHT = 200;

const Investigations: React.FC = () => {
  const navigate = useNavigate();
  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [stats, setStats] = useState<InvestigationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sortBy, _setSortBy] = useState('updatedAt');
  const [sortOrder, _setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchInvestigations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the investigations service to fetch data
      const [investigationsResult, statsData] = await Promise.all([
        investigationsService.getInvestigations({
          search: searchTerm ?? undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          priority: priorityFilter !== 'all' ? priorityFilter : undefined,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          sortBy,
          sortOrder,
          page: 1,
          limit: 20
        }),
        investigationsService.getInvestigationStats()
      ]);

      setInvestigations(investigationsResult.investigations);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch investigations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch investigations');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, priorityFilter, categoryFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchInvestigations();
  }, [fetchInvestigations]);

  // "Overdue Cases" stat card filter — the API has no overdue param, so filter client-side
  const displayedInvestigations = overdueOnly
    ? investigations.filter(
        (inv) =>
          inv.dueDate &&
          inv.status !== 'CLOSED' &&
          new Date(inv.dueDate).getTime() < Date.now()
      )
    : investigations;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'CLOSED': return 'secondary';
      case 'SUSPENDED': return 'danger';
      default: return 'secondary';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL': return 'danger';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'primary';
      case 'LOW': return 'success';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE': return CheckCircleIcon;
      case 'PENDING': return ClockIcon;
      case 'CLOSED': return CheckCircleIcon;
      case 'SUSPENDED': return ExclamationTriangleIcon;
      default: return ClockIcon;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDaysAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) { return '1 day ago'; }
    if (diffDays < 30) { return `${diffDays} days ago`; }
    if (diffDays < 365) { return `${Math.floor(diffDays / 30)} months ago`; }
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Corporate Investigations
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400">
            Manage investigation cases, correlate data, and track patterns
          </p>
        </div>
        <Button
          className="flex items-center space-x-2"
          onClick={() => navigate('/security/investigations/create')}
        >
          <PlusIcon className="w-4 h-4" />
          <span>New Investigation</span>
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={LIST_CONTAINER_VARIANTS}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={STAT_CARD_VARIANTS} className="flex">
            <ToggleStatCard
              label="Total Cases"
              className="flex-1"
              active={statusFilter === 'all' && priorityFilter === 'all' && !overdueOnly}
              onToggle={() => {
                setStatusFilter('all');
                setPriorityFilter('all');
                setOverdueOnly(false);
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Total Cases
                    </p>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {stats.total}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                    <MagnifyingGlassIconSolid className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-xs text-success-600 dark:text-success-400">
                    Active: {stats.active}
                  </span>
                </div>
              </CardContent>
            </ToggleStatCard>
          </motion.div>

          <motion.div variants={STAT_CARD_VARIANTS} className="flex">
            <ToggleStatCard
              label="Critical Priority"
              className="flex-1"
              active={priorityFilter === 'CRITICAL'}
              activeRingClass="ring-danger-500"
              onToggle={() => setPriorityFilter(priorityFilter === 'CRITICAL' ? 'all' : 'CRITICAL')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Critical Priority
                    </p>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {stats.critical}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-danger-100 dark:bg-danger-900/20 rounded-lg flex items-center justify-center">
                    <ExclamationTriangleIconSolid className="w-4 h-4 text-danger-600 dark:text-danger-400" />
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-xs text-danger-600 dark:text-danger-400">
                    Requires immediate attention
                  </span>
                </div>
              </CardContent>
            </ToggleStatCard>
          </motion.div>

          <motion.div variants={STAT_CARD_VARIANTS} className="flex">
            <ToggleStatCard
              label="Avg Resolution"
              className="flex-1"
              active={statusFilter === 'CLOSED'}
              activeRingClass="ring-warning-500"
              onToggle={() => setStatusFilter(statusFilter === 'CLOSED' ? 'all' : 'CLOSED')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Avg Resolution
                    </p>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {stats.averageResolutionTime}d
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-warning-100 dark:bg-warning-900/20 rounded-lg flex items-center justify-center">
                    <ClockIcon className="w-4 h-4 text-warning-600 dark:text-warning-400" />
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-xs text-secondary-600 dark:text-secondary-400">
                    Average days to close
                  </span>
                </div>
              </CardContent>
            </ToggleStatCard>
          </motion.div>

          <motion.div variants={STAT_CARD_VARIANTS} className="flex">
            <ToggleStatCard
              label="Overdue Cases"
              className="flex-1"
              active={overdueOnly}
              activeRingClass="ring-danger-500"
              onToggle={() => setOverdueOnly(!overdueOnly)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 dark:text-secondary-400">
                      Overdue Cases
                    </p>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-white">
                      {stats.openCasesOverdue}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-danger-100 dark:bg-danger-900/20 rounded-lg flex items-center justify-center">
                    <CalendarIcon className="w-4 h-4 text-danger-600 dark:text-danger-400" />
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-xs text-danger-600 dark:text-danger-400">
                    Past due date
                  </span>
                </div>
              </CardContent>
            </ToggleStatCard>
          </motion.div>
        </motion.div>
      )}

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search investigations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="PENDING">Pending</option>
                <option value="CLOSED">Closed</option>
                <option value="SUSPENDED">Suspended</option>
              </select>

              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
              >
                <option value="all">All Priority</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>

              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="pl-3 pr-9 py-2 text-sm border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
              >
                <option value="all">All Categories</option>
                {SECURITY_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              <Button variant="outline" size="sm">
                <FunnelIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Investigations List */}
      <Card>
        <CardHeader>
          <CardTitle>Investigation Cases</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-8">
              <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mx-auto mb-4" />
              <p className="text-danger-600 dark:text-danger-400">{error}</p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => fetchInvestigations()}
              >
                Try Again
              </Button>
            </div>
          ) : displayedInvestigations.length === 0 ? (
            <div className="text-center py-8">
              <MagnifyingGlassIcon className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
              <p className="text-secondary-600 dark:text-secondary-400">No investigations found</p>
            </div>
          ) : (
            <VirtualList
              items={displayedInvestigations}
              itemHeight={INVESTIGATION_ITEM_HEIGHT}
              maxHeight={800}
              overscanCount={3}
              renderItem={(investigation, _index, style) => {
                const StatusIcon = getStatusIcon(investigation.status);
                const containerStyle: CSSProperties = {
                  ...style,
                  paddingBottom: '16px',
                };

                return (
                  <div key={investigation.id} style={containerStyle}>
                    <div
                      className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer h-full"
                      onClick={() => {
                        navigate(`/security/investigations/${investigation.id}`);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                              {investigation.title}
                            </h3>
                            <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                              #{investigation.caseNumber}
                            </span>
                          </div>

                          <div
                            className="text-secondary-600 dark:text-secondary-400 mb-4 line-clamp-2 prose prose-sm dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(investigation.narrative || '') }}
                          />

                          <div className="flex flex-wrap items-center gap-4 text-sm text-secondary-600 dark:text-secondary-400">
                            <div className="flex items-center space-x-1">
                              <UserIcon className="w-4 h-4" />
                              <span>
                                {investigation.leadInvestigator?.firstName ?? 'Unknown'} {investigation.leadInvestigator?.lastName ?? ''}
                              </span>
                            </div>

                            <div className="flex items-center space-x-1">
                              <CalendarIcon className="w-4 h-4" />
                              <span>Created {formatDate(investigation.createdAt)}</span>
                            </div>

                            <div className="flex items-center space-x-1">
                              <ClockIcon className="w-4 h-4" />
                              <span>Updated {formatDaysAgo(investigation.updatedAt)}</span>
                            </div>

                            <div className="flex items-center space-x-1">
                              <DocumentTextIcon className="w-4 h-4" />
                              <span>{investigation.evidence?.documentsCount ?? 0} docs</span>
                            </div>

                            {investigation.relatedCases && investigation.relatedCases.length > 0 && (
                              <div className="flex items-center space-x-1">
                                <LinkIcon className="w-4 h-4" />
                                <span>{investigation.relatedCases.length} related</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col items-end space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getStatusColor(investigation.status)}-100 text-${getStatusColor(investigation.status)}-700 dark:bg-${getStatusColor(investigation.status)}-900/20 dark:text-${getStatusColor(investigation.status)}-400`}>
                              <StatusIcon className="w-3 h-3 inline mr-1" />
                              {investigation.status}
                            </span>

                            <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getPriorityColor(investigation.priority)}-100 text-${getPriorityColor(investigation.priority)}-700 dark:bg-${getPriorityColor(investigation.priority)}-900/20 dark:text-${getPriorityColor(investigation.priority)}-400`}>
                              {investigation.priority}
                            </span>
                          </div>

                          <span className="text-xs text-secondary-500 dark:text-secondary-400">
                            {investigation.category}
                          </span>

                          {investigation.dueDate && (
                            <span className="text-xs text-warning-600 dark:text-warning-400">
                              Due {formatDate(investigation.dueDate)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Investigations;